/**
 * Derived from flubber@0.4.2 and svg-path-properties@0.2.2 (MIT).
 * Adapted for dependency-free internal ESM use in anvil2.
 */
import parse from "./parse";
import { svgPathProperties } from "./path-properties";
import { INVALID_INPUT } from "./errors";
import { isFiniteNumber, type Ring } from "./math";

type PathMeasure = {
  getTotalLength(): number;
  getPointAtLength(length: number): { x: number; y: number };
};

type PathConversionResult = {
  ring: Ring;
  skipBisect?: boolean;
};

export function toPathString(ring: Ring) {
  return `M${ring.join("L")}Z`;
}

export function splitPathString(str: string) {
  return [str];
}

export function pathStringToRing(
  str: string,
  maxSegmentLength?: number,
): PathConversionResult {
  const parsed = parse(str);
  return exactRing(parsed) || approximateRing(str, maxSegmentLength);
}

function exactRing(
  segments: Array<[string, ...number[]]>,
): PathConversionResult | false {
  const ring: Ring = [];
  let currentX = 0;
  let currentY = 0;

  for (let index = 0; index < segments.length; index += 1) {
    const [command, ...values] = segments[index];

    if ((command === "M" || command === "m") && index) {
      break;
    }

    if (command === "Z" || command === "z") {
      break;
    }

    switch (command) {
      case "M":
      case "L":
        currentX = values[0];
        currentY = values[1];
        ring.push([currentX, currentY]);
        break;
      case "m":
      case "l":
        currentX += values[0];
        currentY += values[1];
        ring.push([currentX, currentY]);
        break;
      case "H":
        currentX = values[0];
        ring.push([currentX, currentY]);
        break;
      case "h":
        currentX += values[0];
        ring.push([currentX, currentY]);
        break;
      case "V":
        currentY = values[0];
        ring.push([currentX, currentY]);
        break;
      case "v":
        currentY += values[0];
        ring.push([currentX, currentY]);
        break;
      default:
        return false;
    }
  }

  return ring.length ? { ring } : false;
}

function approximateRing(
  str: string,
  maxSegmentLength?: number,
): PathConversionResult {
  if (!str) {
    throw new TypeError(INVALID_INPUT);
  }

  const measure = getMeasure(str);
  const ring: Ring = [];
  const length = measure.getTotalLength();
  let numPoints = 3;

  if (
    maxSegmentLength &&
    isFiniteNumber(maxSegmentLength) &&
    maxSegmentLength > 0
  ) {
    numPoints = Math.max(numPoints, Math.ceil(length / maxSegmentLength));
  }

  for (let index = 0; index < numPoints; index += 1) {
    const point = measure.getPointAtLength((length * index) / numPoints);
    ring.push([point.x, point.y]);
  }

  return {
    ring,
    skipBisect: true,
  };
}

function getMeasure(d: string): PathMeasure {
  if (typeof window !== "undefined" && window?.document) {
    try {
      const path = window.document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttributeNS(null, "d", d);

      if (
        typeof path.getTotalLength === "function" &&
        typeof path.getPointAtLength === "function"
      ) {
        return path;
      }
    } catch {
      // Fall through to vendored measurement.
    }
  }

  const measure = svgPathProperties(d);

  if (!measure) {
    throw new TypeError(INVALID_INPUT);
  }

  return measure as PathMeasure;
}
